import React, { useRef } from 'react';
import {
  RailsItem,
  RailsThumbnailItem,
} from '@sls-react/typings/services/rails/item';
import randomNumber from '@sls-react/utils/randomNumber';
import classNames from 'classnames';
import getEnv from '@sls-react/utils/getEnv';

interface BoxProps {
  className?: string;
}

const Box: React.FC<BoxProps> = ({ children, className, ...props }) => (
  <div className={classNames(className, 'w-1.5 h-1')} {...props}>
    {children}
  </div>
);

type RailsItemConditionGrade =
  | 'damaged'
  | 'good_worn'
  | 'very_good'
  | 'excellent'
  | 'open_box'
  | 'new';

interface Props {
  item: (RailsItem | RailsThumbnailItem) & {
    condition_grade?: RailsItemConditionGrade;
  };
}

const labelMap: Record<RailsItemConditionGrade, string> = {
  damaged: 'Damaged',
  good_worn: 'Good / Worn',
  very_good: 'Very Good',
  excellent: 'Excellent',
  open_box: 'Open Box',
  new: 'New',
};

function generateCondition(): RailsItemConditionGrade | null {
  const rand = randomNumber(1, 70);

  if (rand < 10) {
    return null;
  } else if (rand < 20) {
    return 'damaged';
  } else if (rand < 30) {
    return 'good_worn';
  } else if (rand < 40) {
    return 'very_good';
  } else if (rand < 50) {
    return 'excellent';
  } else if (rand < 60) {
    return 'open_box';
  } else {
    return 'new';
  }
}

function renderConditionGrade(condition: RailsItemConditionGrade) {
  switch (condition) {
    case 'damaged':
      return (
        <>
          <Box className="bg-red-500" />
          <Box className="bg-red-200" />
          <Box className="bg-red-200" />
          <Box className="bg-red-200" />
          <Box className="bg-red-200" />
          <Box className="bg-red-200" />
        </>
      );
    case 'good_worn':
      return (
        <>
          <Box className="bg-yellow-500" />
          <Box className="bg-yellow-500" />
          <Box className="bg-yellow-200" />
          <Box className="bg-yellow-200" />
          <Box className="bg-yellow-200" />
          <Box className="bg-yellow-200" />
        </>
      );
    case 'very_good':
      return (
        <>
          <Box className="bg-turf-green-500" />
          <Box className="bg-turf-green-500" />
          <Box className="bg-turf-green-500" />
          <Box className="bg-green-200" />
          <Box className="bg-green-200" />
          <Box className="bg-green-200" />
        </>
      );
    case 'excellent':
      return (
        <>
          <Box className="bg-turf-green-500" />
          <Box className="bg-turf-green-500" />
          <Box className="bg-turf-green-500" />
          <Box className="bg-turf-green-500" />
          <Box className="bg-green-200" />
          <Box className="bg-green-200" />
        </>
      );
    case 'open_box':
      return (
        <>
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-200" />
        </>
      );
    case 'new':
      return (
        <>
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
          <Box className="bg-green-500" />
        </>
      );
  }

  return null;
}

const ItemConditionGrade: React.FC<Props> = ({ item }) => {
  const randomConditionRef = useRef<RailsItemConditionGrade | null>(
    typeof window !== 'undefined' && getEnv() !== 'production'
      ? generateCondition()
      : null,
  );

  if (!randomConditionRef.current) {
    return null;
  }

  return (
    <div className="flex flex-col items-end">
      <div className="mb-1 text-xs font-semibold">
        {labelMap[randomConditionRef.current]}
      </div>

      <div className="flex space-x-0.5">
        {renderConditionGrade(randomConditionRef.current)}
      </div>
    </div>
  );
};

export default ItemConditionGrade;
